import { useState, type Dispatch, type SetStateAction } from "react";
import { ActivityIndicator, Pressable, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import { buildTagSeries, formatWeight, type Workout } from "@jackedlogs/shared";

import { MiniChart } from "../charts/MiniChart";
import type { UiTheme } from "../theme";

type TagSeries = ReturnType<typeof buildTagSeries>[number];

// Vertical-only hitSlop (matches ExercisePicker's chip idiom: chip
// minHeight 36 + hitSlop 4, chipRow rowGap 8 ≥ the hitSlop halo so wrapped
// rows can't overlap their neighbour's touch zone — pickerStyles.ts:227-230).
const PILL_SLOP = { top: 4, bottom: 4 } as const;

export type ProgressRecordsCardProps = {
  chartLoading: boolean;
  chartWorkouts: Workout[] | null;
  chartError: string | null;
  tagSeries: TagSeries[];
  visibleTagSeries: TagSeries[];
  tagFilters: string[];
  setTagFilters: Dispatch<SetStateAction<string[]>>;
  localizeTag: (slug: string) => string;
  displayUnit: "kg" | "lb";
  locale: string;
  theme: UiTheme;
};

// Volume-by-tag card for the Progress "more detail" section — the
// muscle/tag volume series with mini charts and a tag filter. Shared
// between mobile and web-rnw (lift/progress-tag-volume-web); the host
// supplies the fetched `chartWorkouts` + derived `tagSeries` (via the
// shared `useChartWorkouts` hook + `buildTagSeries`) and localizes tags
// through its own i18n/taxonomy wiring.
export function ProgressRecordsCard({
  chartLoading,
  chartWorkouts,
  chartError,
  tagSeries,
  visibleTagSeries,
  tagFilters,
  setTagFilters,
  localizeTag,
  displayUnit,
  locale,
  theme,
}: ProgressRecordsCardProps) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  // Collapsed by default — this is the heaviest panel (a chart per tag) and
  // sits last on the page. Tap the header to expand.
  const [expanded, setExpanded] = useState(false);

  return (
    <View style={styles.card}>
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ expanded }}
        onPress={() => setExpanded((v) => !v)}
        style={styles.tagCardHeader}
      >
        <Text style={styles.cardTitle}>
          {t("mobile.progress.tagVolumeTitle")}
        </Text>
        <Text style={styles.tagCardChevron}>{expanded ? "▴" : "▾"}</Text>
      </Pressable>
      {!expanded ? null : (
        <>
          <Text style={styles.cardHelp}>{t("mobile.progress.tagVolumeHelp")}</Text>
          {chartLoading && chartWorkouts === null ? (
            <ActivityIndicator color={theme.palette.ink2} />
          ) : chartError !== null ? (
            <Text style={styles.chartError}>
              {t("mobile.progress.chartLoadFailed")}
            </Text>
          ) : tagSeries.length === 0 ? (
            <Text style={styles.emptyText}>{t("mobile.progress.noTags")}</Text>
          ) : (
            <>
              {tagSeries.length > 1 ? (
                <View style={styles.tagFilterRow}>
                  {tagSeries.map((s) => {
                    const active = tagFilters.includes(s.tag);
                    return (
                      <Pressable
                        key={s.tag}
                        accessibilityRole="button"
                        accessibilityState={{ selected: active }}
                        onPress={() =>
                          setTagFilters((prev) =>
                            active
                              ? prev.filter((x) => x !== s.tag)
                              : [...prev, s.tag],
                          )
                        }
                        style={({ pressed }) => [
                          styles.tagFilterPill,
                          active && styles.tagFilterPillActive,
                          pressed && styles.scopePillPressed,
                        ]}
                        hitSlop={PILL_SLOP}
                      >
                        <Text
                          style={[
                            styles.tagFilterText,
                            active && styles.tagFilterTextActive,
                          ]}
                        >
                          {localizeTag(s.tag)}
                        </Text>
                      </Pressable>
                    );
                  })}
                </View>
              ) : null}
              {visibleTagSeries.map((ts) => {
                const total = ts.volume.reduce((acc, p) => acc + p.y, 0);
                return (
                  <View key={ts.tag} style={styles.tagRow}>
                    <View style={styles.tagRowHead}>
                      <Text style={styles.tagRowTitle} numberOfLines={1}>
                        {localizeTag(ts.tag)}
                      </Text>
                      <Text style={styles.tagRowTotal} numberOfLines={1}>
                        {t("mobile.progress.totalVolume", {
                          weight: formatWeight(total, displayUnit, locale),
                        })}
                      </Text>
                    </View>
                    <MiniChart
                      points={ts.volume}
                      yFormat={(y) => formatWeight(y, displayUnit, locale)}
                      theme={theme}
                    />
                  </View>
                );
              })}
              <Text style={styles.chartFootnote}>
                {t("mobile.progress.chartScope", {
                  n: chartWorkouts?.length ?? 0,
                })}
              </Text>
            </>
          )}
        </>
      )}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    card: {
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r3,
      paddingHorizontal: s.s4,
      paddingVertical: s.s4,
      gap: s.s3,
    },
    tagCardHeader: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      // Explicit floor: a short single-line header wouldn't clear the 44pt
      // touch target on padding alone across every locale (DE/RU run long).
      minHeight: 44,
    },
    cardTitle: {
      color: p.ink,
      fontSize: t.sizes.base,
      fontWeight: "600",
    },
    tagCardChevron: {
      color: p.ink3,
      fontSize: t.sizes.base,
      fontWeight: "600",
    },
    cardHelp: {
      color: p.ink3,
      fontSize: t.sizes.sm,
    },
    scopePillPressed: { opacity: 0.7 },
    emptyText: {
      color: p.ink3,
      fontSize: t.sizes.sm,
      paddingVertical: s.s3,
    },
    chartFootnote: {
      color: p.ink3,
      fontSize: t.sizes.xs,
      paddingTop: s.s2,
    },
    chartError: {
      color: p.danger,
      fontSize: t.sizes.sm,
    },
    // rowGap (s3) ≥ the pill hitSlop halo (4+4) so a wrapped row's touch
    // zones can't overlap the neighbour below it; columns stay tight.
    tagFilterRow: {
      flexDirection: "row",
      flexWrap: "wrap",
      columnGap: s.s2,
      rowGap: s.s3,
    },
    tagFilterPill: {
      paddingHorizontal: s.s3,
      minHeight: 36,
      justifyContent: "center",
      borderRadius: 999,
      borderWidth: 1,
      borderColor: p.border,
      backgroundColor: p.surface,
    },
    tagFilterPillActive: {
      backgroundColor: p.brand500,
      borderColor: p.brand500,
    },
    tagFilterText: {
      color: p.ink2,
      fontSize: t.sizes.xs,
      fontWeight: "600",
    },
    tagFilterTextActive: {
      color: p.inkOnBrand,
    },
    tagRow: {
      gap: s.s2,
      paddingTop: s.s2,
      paddingBottom: s.s3,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderColor: p.border,
    },
    tagRowHead: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "baseline",
      gap: s.s2,
    },
    tagRowTitle: {
      color: p.ink,
      fontSize: t.sizes.sm,
      fontWeight: "600",
      flex: 1,
    },
    tagRowTotal: {
      color: p.ink3,
      fontSize: t.sizes.xs,
      fontVariant: ["tabular-nums"],
    },
  });
}
